import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, CreditCard, FileText, LogIn, LogOut, Pencil, Plus, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { CheckInEvidence, Daycare } from '../../../../../packages/contracts/src/index.js';
import type { ReactNode } from 'react';
import { ActionMenu } from '../../components/ui/action-menu';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { PaginationControls, useControlledPagination } from '../../components/ui/pagination';
import { SearchBox } from '../../components/ui/search';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/state';
import { Table, Td, Th } from '../../components/ui/table';
import { TimeRange } from '../../components/ui/time-range';
import { ListDateFilters } from '../../components/ui/list-date-filters';
import { CheckInEvidenceDialog, type CheckInTarget } from '../../components/check-in/CheckInEvidenceDialog';
import { LegalDocumentsDialog } from '../../components/check-in/LegalDocumentsDialog';
import { ConfirmDialog, type ConfirmState } from '../../components/ui/confirm';
import { api } from '../../shared/api';
import { dueCentsWithFrozenBill, hoursBetween, paidCentsForSource, paymentStatus } from '../../shared/billing';
import { formatDateTime } from '../../shared/format';
import { dateRangeForPreset, overlapsDateRange, useUrlListFilters } from '../../shared/listFilters';
import { matchesSearch } from '../../shared/search';
import { useMemo, useState } from 'react';

const statusLabels: Record<string, string> = {
  reserved: 'Reservada',
  'checked-in': 'Registrada',
  'checked-out': 'Finalizada',
  active: 'Activa',
  completed: 'Completada',
  cancelled: 'Cancelada',
};

const statusChips = [
  { id: '', label: 'Todos' },
  { id: 'reserved', label: 'Reservadas' },
  { id: 'checked-in', label: 'Registradas' },
  { id: 'checked-out', label: 'Finalizadas' },
  { id: 'cancelled', label: 'Canceladas' },
] as const;

export function DaycaresPage() {
  const queryClient = useQueryClient();
  const defaultRange = useMemo(() => dateRangeForPreset('today'), []);
  const { values: filters, setValue, setValues } = useUrlListFilters({ q: '', range: 'today', from: defaultRange.from, to: defaultRange.to, status: '', page: '1', pageSize: '10' }, { keepDefaults: true });
  const search = filters.q;
  const statusFilter = filters.status;
  const [checkInTarget, setCheckInTarget] = useState<CheckInTarget | null>(null);
  const [documentsTarget, setDocumentsTarget] = useState<Daycare | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const page = Math.max(1, Number(filters.page) || 1);
  const pageSize = Math.max(10, Number(filters.pageSize) || 10);
  const daycareListOptions = { limit: search ? 100 : Math.min(page * pageSize + 1, 100), from: filters.from, to: filters.to, status: statusFilter };
  const daycaresQuery = useQuery({ queryKey: ['daycares', daycareListOptions], queryFn: () => api.daycares.list(daycareListOptions) });
  const customersQuery = useQuery({ queryKey: ['customers'], queryFn: () => api.customers.list({ limit: 100 }) });
  const dogsQuery = useQuery({ queryKey: ['dogs'], queryFn: () => api.dogs.list({ limit: 100 }) });
  const paymentsQuery = useQuery({ queryKey: ['payments', 'daycares'], queryFn: () => api.payments.list({ limit: 100 }) });
  const invalidateDaycares = async () => queryClient.invalidateQueries({ queryKey: ['daycares'] });
  const checkInDaycare = useMutation({
    mutationFn: ({ id, evidence }: { readonly id: string; readonly evidence: CheckInEvidence }) => api.daycares.checkIn(id, { evidence }),
    onSuccess: async () => {
      setCheckInTarget(null);
      await invalidateDaycares();
      await queryClient.invalidateQueries({ queryKey: ['membership-purchases'] });
    },
  });
  const checkOutDaycare = useMutation({ mutationFn: api.daycares.checkOut, onSuccess: invalidateDaycares });
  const deleteDaycare = useMutation({ mutationFn: api.daycares.remove, onSuccess: invalidateDaycares });

  const daycares = daycaresQuery.data?.items ?? [];
  const customers = customersQuery.data?.items ?? [];
  const dogs = dogsQuery.data?.items ?? [];
  const filteredDaycares = daycares.filter((daycare) => {
    const customer = customers.find((item) => item.id === daycare.customerId);
    const dogNames = dogIdsForDaycare(daycare).map((dogId) => dogs.find((item) => item.id === dogId)?.name ?? dogId);
    if (!overlapsDateRange(daycare.startAt, daycare.endAt, filters.from, filters.to)) return false;
    if (statusFilter && normalizeDaycareStatus(daycare.status) !== statusFilter) return false;
    return matchesSearch(search, [customer?.firstName, customer?.lastName, ...dogNames, daycare.startAt, daycare.endAt, statusLabels[daycare.status]]);
  });
  const daycaresPage = useControlledPagination(filteredDaycares, page, (nextPage) => setValue('page', String(nextPage)), pageSize);
  if (daycaresQuery.isLoading || customersQuery.isLoading || dogsQuery.isLoading || paymentsQuery.isLoading) return <LoadingState label="Cargando guarderia" />;
  if (daycaresQuery.isError) return <ErrorState message={daycaresQuery.error.message} />;
  if (customersQuery.isError) return <ErrorState message={customersQuery.error.message} />;
  if (dogsQuery.isError) return <ErrorState message={dogsQuery.error.message} />;
  if (paymentsQuery.isError) return <ErrorState message={paymentsQuery.error.message} />;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-slate-950">Guarderia</h1>
        <p className="mt-1 text-sm text-slate-500">Visitas por hora o paquetes de entradas para guarderia.</p>
      </div>
      <div className="flex justify-start">
        <Link className="inline-flex h-9 items-center gap-2 rounded-md border border-[#d6ded0] bg-white px-3 text-sm font-medium text-[#334033] hover:bg-[#eef3ea]" to="/admin/daycares/new">
          <Plus className="h-4 w-4" aria-hidden="true" />
          Nueva guarderia
        </Link>
      </div>
      <Card>
        <CardHeader><h2 className="text-sm font-semibold">Registro de guarderias</h2></CardHeader>
        <CardContent>
          <div className="mb-3 flex justify-start">
            <SearchBox value={search} onChange={(value) => setValue('q', value)} placeholder="Buscar guarderia" label="Buscar guarderia" />
          </div>
          <ListDateFilters range={filters.range} from={filters.from} to={filters.to} onChange={setValues} startId="daycaresFrom" endId="daycaresTo" />
          <div className="my-3">
            <SegmentedControl value={statusFilter} options={statusChips} onChange={(nextStatus) => setValue('status', nextStatus)} ariaLabel="Filtrar guarderias por estado" className="sm:max-w-3xl" />
          </div>
          {daycares.length === 0 ? <EmptyState title="Aun no hay guarderias" body="Crea una visita de guarderia para empezar a controlar entradas por hora." /> : null}
          {daycares.length > 0 && filteredDaycares.length === 0 ? <EmptyState title="Sin resultados" body="Intenta con otro cliente, perro, fecha o estado." /> : null}
          {filteredDaycares.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <thead>
                  <tr>
                    <Th>Concepto</Th>
                    <Th>Cliente</Th>
                    <Th>Perro</Th>
                    <Th>Horario</Th>
                    <Th>Uso</Th>
                    <Th>Estado</Th>
                    <Th>Pago</Th>
                    <Th>Acciones</Th>
                  </tr>
                </thead>
                <tbody>
                  {daycaresPage.pageItems.map((daycare) => {
                    const customer = customers.find((item) => item.id === daycare.customerId);
                    const dogNames = dogIdsForDaycare(daycare).map((dogId) => dogs.find((item) => item.id === dogId)?.name ?? dogId);
                    const dogName = dogNames.join(', ');
                    const status = daycare.status as string;
                    const plannedHours = hoursBetween(daycare.startAt, daycare.endAt ?? daycare.startAt);
                    const isPackage = daycare.billingMode === 'entrance-package';
                    const entrancesUsed = daycare.entrancesUsed ?? 0;
                    const entranceLimit = daycare.entranceLimit ?? 0;
                    const exitsRegistered = entrancesUsed;
                    const entrancesLeft = Math.max(0, entranceLimit - entrancesUsed);
                    const hasEntrancesLeft = !isPackage || entrancesUsed < entranceLimit;
                    const hasExceededTime = isPackage && ['checked-in', 'active'].includes(status) && hasExceededExpectedExit(daycare.endAt);
                    const dueCents = dueCentsWithFrozenBill(paymentsQuery.data?.items ?? [], 'daycare', daycare.id, 0);
                    const paidCents = paidCentsForSource(paymentsQuery.data?.items ?? [], 'daycare', daycare.id);
                    const payStatus = paymentStatus(dueCents, paidCents);
                    const conceptId = paymentConceptId('daycare', daycare.id);
                    return (
                      <tr key={daycare.id}>
                        <Td><span className="font-mono text-xs">{conceptId}</span></Td>
                        <Td className="font-medium text-slate-900">{customer ? <Link className="text-[#2f6b38] hover:underline" to={`/admin/customers/${customer.id}/edit`}>{customer.firstName} {customer.lastName}</Link> : daycare.customerId}</Td>
                        <Td>{dogName}</Td>
                        <Td>
                          <div className="grid gap-1">
                            <TimeRange start={daycare.startAt} end={daycare.endAt} />
                            {hasExceededTime ? <span className="rounded-md bg-[#fff3d6] px-2 py-1 text-xs font-semibold text-[#7a5a16]">Tiempo excedido</span> : null}
                          </div>
                        </Td>
                        <Td>{isPackage ? <EntranceUsage used={entrancesUsed} exits={exitsRegistered} limit={entranceLimit} left={entrancesLeft} /> : `${plannedHours} h`}</Td>
                        <Td><Badge>{statusLabels[status] ?? status}</Badge></Td>
                        <Td>{daycare.prepaidByMembership ? <Badge className="bg-[#e5f3e4] text-[#2f6b38]">Prepago membresia</Badge> : <Badge className={paymentToneClass(payStatus.tone)}>{payStatus.label}</Badge>}</Td>
                        <Td>
                          <ActionMenu groups={[
                            {
                              label: 'Flujo',
                              children: <>
                                <MenuButton disabled={status !== 'reserved' || !hasEntrancesLeft || checkInDaycare.isPending} onClick={() => setCheckInTarget({ id: daycare.id, kind: 'daycare', label: `Guarderia - ${dogName}` })} icon={<LogIn className="h-4 w-4" aria-hidden="true" />}>Registrar entrada</MenuButton>
                                <MenuButton
                                  disabled={!['checked-in', 'active'].includes(status) || checkOutDaycare.isPending}
                                  onClick={() => {
                                    if (daycare.prepaidByMembership && isDifferentLocalDay(daycare.startAt, new Date())) {
                                      setConfirm({
                                        title: 'Salida en dia diferente',
                                        body: `Esta guarderia de membresia inicio el ${formatDateTime(daycare.startAt)} y la salida se registrara hoy. Confirma si quieres cerrarla de todos modos.`,
                                        confirmLabel: 'Confirmar',
                                        tone: 'warning',
                                        onConfirm: () => checkOutDaycare.mutate(daycare.id),
                                      });
                                      return;
                                    }
                                    checkOutDaycare.mutate(daycare.id);
                                  }}
                                  icon={<LogOut className="h-4 w-4" aria-hidden="true" />}
                                >
                                  Confirmar salida
                                </MenuButton>
                              </>,
                            },
                            {
                              label: 'Gestion',
                              children: <>
                                <MenuButton onClick={() => setDocumentsTarget(daycare)} icon={<FileText className="h-4 w-4" aria-hidden="true" />}>Documentos legales</MenuButton>
                                <MenuLink to={`/admin/daycares/${daycare.id}/edit`} icon={<Pencil className="h-4 w-4" aria-hidden="true" />}>Editar guarderia</MenuLink>
                                {daycare.prepaidByMembership ? null : <MenuLink to={`/admin/payments?customerId=${encodeURIComponent(daycare.customerId)}&sourceType=daycare&sourceId=${encodeURIComponent(daycare.id)}`} icon={<CreditCard className="h-4 w-4" aria-hidden="true" />}>Ir a pagos</MenuLink>}
                              </>,
                            },
                            {
                              label: 'Riesgo',
                              tone: 'danger',
                              children: <MenuButton danger disabled={deleteDaycare.isPending} onClick={() => setConfirm({ title: 'Eliminar guarderia', body: `Eliminar la guarderia de ${dogName}?`, confirmLabel: 'Eliminar', onConfirm: () => deleteDaycare.mutate(daycare.id) })} icon={<Trash2 className="h-4 w-4" aria-hidden="true" />}>Eliminar</MenuButton>,
                            },
                          ]} />
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          ) : null}
          <PaginationControls {...daycaresPage} onPageChange={daycaresPage.setPage} onPageSizeChange={(nextPageSize) => setValues({ pageSize: String(nextPageSize), page: '1' })} />
          {checkInDaycare.isError ? <p className="mt-3 text-sm text-rose-600">{checkInDaycare.error.message}</p> : null}
          {checkOutDaycare.isError ? <p className="mt-3 text-sm text-rose-600">{checkOutDaycare.error.message}</p> : null}
          {deleteDaycare.isError ? <p className="mt-3 text-sm text-rose-600">{deleteDaycare.error.message}</p> : null}
        </CardContent>
      </Card>
      <CheckInEvidenceDialog
        target={checkInTarget}
        onClose={() => setCheckInTarget(null)}
        onSubmit={async (evidence) => { await checkInDaycare.mutateAsync({ id: checkInTarget?.id ?? '', evidence }); }}
      />
      {documentsTarget ? (
        <LegalDocumentsDialog
          title={`Guarderia - ${dogIdsForDaycare(documentsTarget).map((dogId) => dogs.find((dog) => dog.id === dogId)?.name ?? dogId).join(', ')}`}
          documents={documentsTarget.checkInDocuments ?? []}
          onClose={() => setDocumentsTarget(null)}
          onClear={async () => {
            await api.daycares.update(documentsTarget.id, { checkInDocuments: [] });
            await queryClient.invalidateQueries({ queryKey: ['daycares'] });
          }}
        />
      ) : null}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function EntranceUsage({ used, exits, limit, left }: { readonly used: number; readonly exits: number; readonly limit: number; readonly left: number }) {
  const blocked = left <= 0;
  return (
    <div className={`grid gap-1 rounded-md border px-2.5 py-2 text-xs ${blocked ? 'border-[#f1c3c3] bg-[#fff8f8] text-[#8a4a3a]' : 'border-[#d6ded0] bg-[#fbfcfa] text-[#334033]'}`}>
      <span className="inline-flex items-center gap-1 font-semibold">
        {blocked ? <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" /> : <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />}
        {left} disponibles
      </span>
      <span>Entradas: {used} de {limit}</span>
      <span>Salidas: {exits}</span>
    </div>
  );
}

function paymentConceptId(sourceType: 'stay' | 'daycare' | 'service', sourceId: string) {
  const prefix = sourceType === 'stay' ? 'EST' : sourceType === 'daycare' ? 'GUA' : 'SER';
  return `${prefix}-${sourceId.replace(/[^a-z0-9]/gi, '').slice(-6).toUpperCase()}`;
}

function paymentToneClass(tone: 'paid' | 'partial' | 'pending') {
  if (tone === 'paid') return 'bg-[#e5f3e4] text-[#2f6b38]';
  if (tone === 'partial') return 'bg-[#fff3d6] text-[#7a5a16]';
  return 'bg-[#f3e5df] text-[#8a4a3a]';
}

function dogIdsForDaycare(daycare: { dogId: string; dogIds?: readonly string[] }) {
  return daycare.dogIds?.length ? daycare.dogIds : [daycare.dogId];
}

function normalizeDaycareStatus(status: string) {
  if (status === 'active') return 'checked-in';
  if (status === 'completed') return 'checked-out';
  return status;
}

function hasExceededExpectedExit(endAt?: string) {
  if (!endAt) return false;
  const end = new Date(endAt).getTime();
  return Number.isFinite(end) && Date.now() > end;
}

function isDifferentLocalDay(startAt: string, end: Date) {
  const start = new Date(startAt);
  return start.toLocaleDateString('en-CA') !== end.toLocaleDateString('en-CA');
}

function MenuLink({ to, icon, children }: { readonly to: string; readonly icon: ReactNode; readonly children: ReactNode }) {
  return <Link className="flex items-center gap-2 rounded-md px-2 py-2 text-[#334033] hover:bg-[#eef3ea]" to={to}>{icon}{children}</Link>;
}

function MenuButton({ children, danger, disabled, icon, onClick }: { readonly children: ReactNode; readonly danger?: boolean; readonly disabled?: boolean; readonly icon: ReactNode; readonly onClick: () => void }) {
  return <button type="button" className={`flex items-center gap-2 rounded-md px-2 py-2 text-left disabled:cursor-not-allowed disabled:opacity-50 ${danger ? 'text-[#8a4a3a] hover:bg-[#fff8f8]' : 'text-[#334033] hover:bg-[#eef3ea]'}`} disabled={disabled} onClick={onClick}>{icon}{children}</button>;
}
